import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import RemboLogo from "./components/RemboLogo";
import Icon from "./components/icons/Icon";
import Avatar from "./components/ui/Avatar";
import Button from "./components/ui/Button";
import useSEO from "./hooks/useSEO";
import { useUser } from "./user_context";
import "./styles/list.css";
import "./styles/page.css";
import "./css/landing_page.css";

// ─── Data ─────────────────────────────────────────────────────────────────────
const disciplines = [
  "Hair styling", "Nail technology", "Skincare & aesthetics", "Makeup artistry",
  "Barbering", "Spa & wellness", "Hospitality", "Beauty education", "Salon management",
];

const steps = [
  {
    icon: "profile",
    tone: "rose",
    title: "Set up your profile",
    text: "Employers post their business and open roles. Professionals build a profile that shows off their skills and specialisations, not just a CV.",
  },
  {
    icon: "compass",
    tone: "indigo",
    title: "Find what fits",
    text: "Browse roles tailored to the beauty and hospitality world, or let relevant listings come to you. Filter by location, discipline, and availability.",
  },
  {
    icon: "shortlist",
    tone: "mint",
    title: "Make the connection",
    text: "Apply, shortlist, and communicate, all in one place. From first contact to confirmed hire, we keep the process simple and human.",
  },
];

const employers = [
  "Salons and barbershops",
  "Spas and wellness centres",
  "Aesthetic and skincare clinics",
  "Beauty brands and distributors",
  "Beauty shops and cosmetics retailers",
  "Restaurants, hotels and resorts",
  "Training institutions",
];

const jobSeekers = [
  "Beauty therapists and aestheticians",
  "Nail technicians and barbers",
  "Makeup artists",
  "Beauty advisors and sales staff",
  "Receptionists and front-of-house",
  "Trainers and educators",
  "Hospitality professionals",
  "Students and recent graduates",
];

const testimonials = [
  {
    text: "Within two weeks of posting, I had three qualified stylists apply. Rembolist understands our industry in a way generic job boards simply don't.",
    name: "Amara Osei",
    role: "Salon owner, Nairobi",
  },
  {
    text: "I'd been freelancing for years but wanted something steadier. Rembolist matched me with a spa that fit my specialty perfectly. Life-changing.",
    name: "Fatima Al-Rashidi",
    role: "Esthetician",
  },
  {
    text: "Every applicant actually understood the industry. We hired our head nail technician within a month, something I couldn't manage on other platforms.",
    name: "Chloe Mwangi",
    role: "Spa director",
  },
];

// Illustrative rows for the hero preview (not real people or data).
const previewRows = [
  { name: "Amara Okafor", role: "Senior Hair Stylist", when: "2 hours ago" },
  { name: "Wanjiku Mwangi", role: "Nail Technician", when: "5 hours ago" },
  { name: "Zuri Hassan", role: "Makeup Artist", when: "Yesterday" },
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const rootRef = useRef(null);
  const navigate = useNavigate();
  const { userData, loading } = useUser();
  const accountPath = userData?.role === "employer" ? "/employer/dashboard" : "/dashboard";

  // Anonymous visitors clicking these CTAs used to be sent straight to a
  // protected /dashboard route and silently bounced to /signin, a dead end
  // for a first-time visitor with no account yet. Signed-in visitors go
  // straight to their own dashboard; everyone else goes to sign-up
  // pre-selected for the role they clicked.
  const goToPortal = (targetRole) => {
    if (userData) {
      navigate(accountPath);
    } else {
      navigate(`/signup?role=${targetRole}`);
    }
  };

  useSEO({
    title: "Beauty, Wellness & Hospitality Jobs",
    description: "RemboList connects employers and job seekers across the beauty, wellness, and hospitality industries in Kenya — find work or hire talent today.",
    path: "/",
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll reveal: elements marked data-reveal fade/slide in once as they enter
  // the viewport. Skipped entirely for reduced-motion users or old browsers.
  // useLayoutEffect so the hidden start state is applied before the first
  // paint: the hero then animates in on load instead of flashing and hiding.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !("IntersectionObserver" in window)) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    root.classList.add("lp-anim");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("is-in"); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -6% 0px" });
    root.querySelectorAll("[data-reveal]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="rl-landing" ref={rootRef}>
      <a href="#lp-main" className="rl-skip-link">Skip to content</a>

      {/* NAV */}
      <header className={`lp-nav${scrolled ? " lp-nav--scrolled" : ""}`}>
        <div className="lp-nav__inner">
          <Link to="/" className="lp-nav__logo" aria-label="RemboList home">
            <RemboLogo theme="light" />
          </Link>
          <nav className="lp-nav__links" aria-label="Main">
            <a href="#about">About</a>
            <a href="#how">How it works</a>
            <a href="#who">Who we serve</a>
          </nav>
          <div className="lp-nav__actions">
            {loading ? null : userData ? (
              <Button size="sm" icon="overview" onClick={() => navigate(accountPath)}>View Account</Button>
            ) : (
              <>
                <Button size="sm" variant="tertiary" onClick={() => navigate("/signin")}>Sign in</Button>
                <Button size="sm" onClick={() => navigate("/signup")}>Get started</Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main id="lp-main">
        {/* HERO */}
        <section className="lp-hero">
          <div className="lp-hero__copy">
            <p className="rl-eyebrow rl-eyebrow--accent" data-reveal="up">Beauty · Wellness · Hospitality</p>
            <h1 className="lp-hero__title" data-reveal="up" style={{ "--d": "80ms" }}>
              Where the right people find the right places
              <Icon name="sparkle" size={36} className="lp-hero__spark" />
            </h1>
            <p className="lp-hero__body" data-reveal="up" style={{ "--d": "180ms" }}>
              RemboList is a dedicated job board for the beauty, wellness, and
              hospitality industries, built to make hiring less painful and job
              searching far more targeted. No more irrelevant listings.
            </p>
            <div className="lp-hero__actions" data-reveal="up" style={{ "--d": "280ms" }}>
              <Button size="lg" icon="compass" onClick={() => goToPortal("jobseeker")}>Find work</Button>
              <Button size="lg" variant="secondary" icon="add" onClick={() => goToPortal("employer")}>Post a job</Button>
            </div>
          </div>

          {/* Decorative preview of the in-app dashboard */}
          <div className="lp-preview" aria-hidden="true" data-reveal="right" style={{ "--d": "200ms" }}>
            <div className="lp-preview__stats">
              <div className="rl-stat-card">
                <div className="rl-stat-card__top">
                  <span className="rl-stat-card__label">Active job posts</span>
                  <span className="rl-icon-tile rl-tone--rose"><Icon name="briefcase" size={20} /></span>
                </div>
                <div className="rl-stat-card__figure">
                  <span className="rl-stat-card__value">03</span>
                  <span className="rl-stat-card__caption">1 closed</span>
                </div>
              </div>
              <div className="rl-stat-card">
                <div className="rl-stat-card__top">
                  <span className="rl-stat-card__label">Total applicants</span>
                  <span className="rl-icon-tile rl-tone--indigo"><Icon name="people" size={20} /></span>
                </div>
                <div className="rl-stat-card__figure">
                  <span className="rl-stat-card__value">24</span>
                  <span className="rl-stat-card__caption">across all posts</span>
                </div>
              </div>
            </div>
            <div className="rl-card lp-preview__list">
              <div className="rl-card__header rl-card__header--ruled">
                <div className="rl-card__heading">
                  <h2 className="rl-card__title">Recent applications</h2>
                  <p className="rl-card__subtitle">Talented people interested in your roles</p>
                </div>
              </div>
              {previewRows.map((r) => (
                <div key={r.name} className="rl-list-row">
                  <Avatar size={48} name={r.name} />
                  <div className="rl-list-row__body">
                    <div className="rl-list-row__title">{r.name}</div>
                    <div className="rl-list-row__sub">{r.role}</div>
                  </div>
                  <span className="rl-list-row__time">{r.when}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* DISCIPLINES */}
        <section className="lp-chips" aria-label="Disciplines we cover">
          {disciplines.map((d, i) => (
            <span key={d} data-reveal="pop" style={{ "--d": `${i * 60}ms` }} className={`lp-chip rl-tone--${["rose", "indigo", "mint", "amber"][i % 4]}`}>{d}</span>
          ))}
        </section>

        {/* ABOUT */}
        <section className="lp-section lp-about" id="about">
          <div>
            <p className="rl-eyebrow rl-eyebrow--accent">About RemboList</p>
            <h2 className="lp-title" data-reveal="up">A job board built for this industry</h2>
            <p className="lp-body">
              RemboList is a specialised platform for the beauty, wellness, and
              hospitality sectors. Generic job boards are built for everyone,
              which means they're rarely built for you. We exist to fix that gap:
              giving businesses a faster way to find qualified professionals, and
              giving job seekers a space where their skills are actually
              understood.
            </p>
            <p className="lp-body">
              Whether you're a salon owner tired of CVs that don't fit, or a
              beauty therapist looking for something more than a broad search
              engine, RemboList was made with you in mind.
            </p>
          </div>
          <div className="lp-mission" data-reveal="up">
            <Icon name="sparkle" size={28} className="lp-mission__spark" />
            <p className="lp-mission__quote">
              Our mission is simple: to make hiring easier and help talented
              professionals connect with the right opportunities, not just any
              opportunities.
            </p>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="lp-section lp-section--tint" id="how">
          <p className="rl-eyebrow rl-eyebrow--accent">How it works</p>
          <h2 className="lp-title" data-reveal="up">Straightforward, by design</h2>
          <div className="lp-grid lp-grid--3">
            {steps.map((s, i) => (
              <div key={s.title} className="rl-card lp-step" data-reveal="up" style={{ "--d": `${i * 120}ms` }}>
                <div className="lp-step__top">
                  <span className={`rl-icon-tile rl-icon-tile--lg rl-tone--${s.tone}`}><Icon name={s.icon} size={26} /></span>
                  <span className="lp-step__num" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="lp-step__title">{s.title}</h3>
                <p className="lp-step__text">{s.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* WHO WE SERVE */}
        <section className="lp-section" id="who">
          <p className="rl-eyebrow rl-eyebrow--accent">Who we serve</p>
          <h2 className="lp-title" data-reveal="up">Two sides of the same industry</h2>
          <div className="lp-grid lp-grid--2">
            <div className="rl-card lp-serve" data-reveal="up">
              <span className="rl-icon-tile rl-tone--rose"><Icon name="company" size={20} /></span>
              <p className="rl-eyebrow">For employers</p>
              <h3 className="lp-serve__title">Businesses &amp; institutions</h3>
              <p className="lp-serve__body">
                Stop sorting through applications that don't match. Post your
                roles once and reach professionals who actually work in your
                field, from independent salons to hotel groups.
              </p>
              <ul className="lp-list">
                {employers.map((item) => (
                  <li key={item}><Icon name="check-circle" size={18} />{item}</li>
                ))}
              </ul>
              <Button variant="secondary" iconRight="forward" onClick={() => goToPortal("employer")}>Start hiring</Button>
            </div>
            <div className="rl-card lp-serve">
              <span className="rl-icon-tile rl-tone--indigo"><Icon name="profile" size={20} /></span>
              <p className="rl-eyebrow">For job seekers</p>
              <h3 className="lp-serve__title">Beauty &amp; hospitality professionals</h3>
              <p className="lp-serve__body">
                Find roles that actually match your background, whether you're
                just starting out, switching specialisations, or looking for
                something senior.
              </p>
              <ul className="lp-list">
                {jobSeekers.map((item) => (
                  <li key={item}><Icon name="check-circle" size={18} />{item}</li>
                ))}
              </ul>
              <Button variant="secondary" iconRight="forward" onClick={() => goToPortal("jobseeker")}>Find work</Button>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="lp-section lp-section--tint">
          <p className="rl-eyebrow rl-eyebrow--accent">Success stories</p>
          <h2 className="lp-title" data-reveal="up">Hear from our users</h2>
          <div className="lp-grid lp-grid--3">
            {testimonials.map((t, ti) => (
              <figure key={t.name} className="rl-card lp-testi" data-reveal="up" style={{ "--d": `${ti * 120}ms` }}>
                <blockquote className="lp-testi__text">{t.text}</blockquote>
                <figcaption className="lp-testi__author">
                  <Avatar size={48} name={t.name} />
                  <span>
                    <span className="lp-testi__name">{t.name}</span>
                    <span className="lp-testi__role">{t.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="lp-section lp-cta-wrap">
          <div className="lp-cta" data-reveal="zoom">
            <p className="rl-eyebrow lp-cta__eyebrow">Get started</p>
            <h2 className="lp-cta__title">Your next step starts here</h2>
            <p className="lp-cta__body">
              Whether you're building a team or building a career, RemboList is
              the dedicated platform the beauty, wellness, and hospitality
              industries have needed.
            </p>
            <div className="lp-cta__buttons">
              <button type="button" className="lp-cta__btn lp-cta__btn--solid" onClick={() => goToPortal("jobseeker")}>
                I'm looking for work
              </button>
              <button type="button" className="lp-cta__btn lp-cta__btn--ghost" onClick={() => goToPortal("employer")}>
                I'm hiring talent
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-footer__inner">
          <div className="lp-footer__brand">
            <RemboLogo theme="light" />
            <p>The dedicated job board for beauty, wellness, and hospitality across East Africa.</p>
          </div>
          <div>
            <p className="rl-eyebrow">Platform</p>
            <ul className="lp-footer__links">
              <li><button type="button" onClick={() => goToPortal("jobseeker")}>Browse jobs</button></li>
              <li><button type="button" onClick={() => goToPortal("employer")}>Post a job</button></li>
              <li><a href="#about">About us</a></li>
            </ul>
          </div>
        </div>
        <div className="lp-footer__bottom">
          <span>© 2026 RemboList. All rights reserved.</span>
          <span className="lp-footer__legal">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
          </span>
        </div>
      </footer>
    </div>
  );
}
